/**
 * Publisher API feature — data layer (FS-142 / DASH).
 *
 * Thin typed wrappers over `apiClient` for the four admin endpoints exposed
 * by the `/api/v1/publisher-api` router:
 *
 *   GET  /scopes                 — canonical scope list for the UI editor.
 *   POST /keys                   — mint a new key (one-time raw reveal).
 *   GET  /keys/:affiliate_id     — masked list (never contains key_hash).
 *   POST /revoke/:id             — soft-revoke.
 *
 * No React / TanStack imports here so the page test file can exercise the
 * validators and the fetch surface independently. The page itself drives a
 * small useState hydration pattern mirroring the advertiser-portal landing.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Scope constants — mirror the API's AVAILABLE_SCOPES tuple. We keep a
// hand-rolled copy here so the page test does not import from the worker
// source tree (which would bring in Hono + drizzle during Vite transform).
// ---------------------------------------------------------------------------

export const AVAILABLE_SCOPES = ["campaigns", "stats", "creatives"] as const;
export type PublisherScope = (typeof AVAILABLE_SCOPES)[number];

export function isValidScope(s: unknown): s is PublisherScope {
	return (
		typeof s === "string" &&
		(AVAILABLE_SCOPES as readonly string[]).includes(s)
	);
}

// ---------------------------------------------------------------------------
// Row / response shapes.
// ---------------------------------------------------------------------------

export interface PublisherApiKeyMasked {
	id: string;
	tenant_id: string;
	affiliate_id: string;
	key_prefix: string;
	scopes: PublisherScope[];
	label: string | null;
	last_used_at: number | null;
	revoked_at: number | null;
	created_at: number;
}

export interface PublisherApiKeyCreatedResponse {
	key: PublisherApiKeyMasked;
	raw_key: string;
}

export interface PublisherApiKeysListResponse {
	affiliate_id: string;
	count: number;
	keys: PublisherApiKeyMasked[];
}

export interface PublisherApiScopesResponse {
	scopes: PublisherScope[];
}

export interface PublisherApiRevokedResponse {
	id: string;
	revoked_at: number;
	revoked: boolean;
}

// ---------------------------------------------------------------------------
// Client-side form validation — mirrors the API's `validateCreateKeyBody`.
// ---------------------------------------------------------------------------

const AFFILIATE_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

export interface CreateKeyFormInput {
	affiliateId: string;
	scopes: string[];
	label?: string;
}

export interface CreateKeyFormData {
	affiliate_id: string;
	scopes: PublisherScope[];
	label?: string;
}

export interface CreateKeyParseOk {
	success: true;
	data: CreateKeyFormData;
}
export interface CreateKeyParseErr {
	success: false;
	errors: Record<string, string>;
}
export type CreateKeyParseResult = CreateKeyParseOk | CreateKeyParseErr;

export const createKeyFormSchema = {
	safeParse(input: CreateKeyFormInput): CreateKeyParseResult {
		const errors: Record<string, string> = {};

		const affiliateRaw =
			typeof input.affiliateId === "string" ? input.affiliateId.trim() : "";
		if (affiliateRaw.length === 0) {
			errors.affiliateId = "Affiliate ID is required.";
		} else if (!AFFILIATE_ID_RE.test(affiliateRaw)) {
			errors.affiliateId =
				"Affiliate ID may only contain letters, numbers, '-' and '_'.";
		}

		const cleanedScopes: PublisherScope[] = [];
		if (!Array.isArray(input.scopes) || input.scopes.length === 0) {
			errors.scopes = "Pick at least one scope.";
		} else {
			for (const raw of input.scopes) {
				if (!isValidScope(raw)) {
					errors.scopes = `Unknown scope: ${String(raw)}`;
					break;
				}
				if (!cleanedScopes.includes(raw)) cleanedScopes.push(raw);
			}
		}

		let label: string | undefined;
		if (typeof input.label === "string") {
			const trimmed = input.label.trim();
			if (trimmed.length > 128) {
				errors.label = "Label must be 128 characters or fewer.";
			} else if (trimmed.length > 0) {
				label = trimmed;
			}
		}

		if (Object.keys(errors).length > 0) return { success: false, errors };
		return {
			success: true,
			data: {
				affiliate_id: affiliateRaw,
				scopes: cleanedScopes,
				label,
			},
		};
	},
};

// ---------------------------------------------------------------------------
// API wrappers.
// ---------------------------------------------------------------------------

export async function fetchScopes(): Promise<PublisherScope[]> {
	try {
		const res = await apiClient.get<PublisherApiScopesResponse>(
			"/publisher-api/scopes",
		);
		return Array.isArray(res?.scopes)
			? res.scopes.filter(isValidScope)
			: [...AVAILABLE_SCOPES];
	} catch (err) {
		if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
			return [...AVAILABLE_SCOPES];
		}
		throw err;
	}
}

export async function fetchKeys(
	affiliateId: string,
): Promise<PublisherApiKeyMasked[]> {
	const id = affiliateId.trim();
	if (!id) return [];
	try {
		const res = await apiClient.get<PublisherApiKeysListResponse>(
			`/publisher-api/keys/${encodeURIComponent(id)}`,
		);
		return Array.isArray(res?.keys) ? res.keys : [];
	} catch (err) {
		if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
			return [];
		}
		throw err;
	}
}

export async function createKey(
	body: CreateKeyFormData,
): Promise<PublisherApiKeyCreatedResponse> {
	return apiClient.post<PublisherApiKeyCreatedResponse>(
		"/publisher-api/keys",
		{
			affiliate_id: body.affiliate_id,
			scopes: body.scopes,
			label: body.label,
		},
	);
}

export async function revokeKey(
	id: string,
): Promise<PublisherApiRevokedResponse> {
	return apiClient.post<PublisherApiRevokedResponse>(
		`/publisher-api/revoke/${encodeURIComponent(id)}`,
	);
}

// ---------------------------------------------------------------------------
// Formatters — pure helpers used by the page + the tests.
// ---------------------------------------------------------------------------

export function formatLastUsed(
	row: Pick<PublisherApiKeyMasked, "last_used_at">,
	now: number = Date.now(),
): string {
	if (!row.last_used_at) return "never";
	const deltaSec = Math.max(0, Math.floor(now / 1000) - row.last_used_at);
	if (deltaSec < 60) return `${deltaSec}s ago`;
	const mins = Math.floor(deltaSec / 60);
	if (mins < 60) return `${mins}m ago`;
	const hours = Math.floor(mins / 60);
	if (hours < 24) return `${hours}h ago`;
	const days = Math.floor(hours / 24);
	return `${days}d ago`;
}

export function formatKeyStatus(row: PublisherApiKeyMasked): "active" | "revoked" {
	return row.revoked_at ? "revoked" : "active";
}

export function maskedKeyDisplay(row: PublisherApiKeyMasked): string {
	const prefix = row.key_prefix.slice(0, 8);
	return `${prefix}........`;
}

export const publisherApiPaths = {
	scopes: "/publisher-api/scopes",
	keys: "/publisher-api/keys",
	revoke: "/publisher-api/revoke",
	// FS-226
	v2Keys: "/publisher-api/v2/keys",
	v2KeysList: "/publisher-api/keys",
	endpoints: "/publisher-api/endpoints",
	accessLog: "/publisher-api/access-log",
} as const;

// ===========================================================================
// FS-226 — publisher-scoped umbrella API wrappers.
//
// The FS-226 surface lives on the same `/publisher-api` router as FS-142 but
// addresses publisher_id (not affiliate_id), returns a masked `key_preview`
// rather than `key_prefix`, uses canonical scope identifiers like
// `campaigns.list_approved`, and adds access-log + endpoint-catalog reads.
// ===========================================================================

export const PUBLISHER_SCOPES_V2 = [
	"campaigns.list_approved",
	"clicks.read",
	"conversions.read",
	"payouts.read",
	"tracking_links.read",
] as const;
export type PublisherScopeV2 = (typeof PUBLISHER_SCOPES_V2)[number];

export function isValidScopeV2(s: unknown): s is PublisherScopeV2 {
	return (
		typeof s === "string" &&
		(PUBLISHER_SCOPES_V2 as readonly string[]).includes(s)
	);
}

export interface PublisherApiKeyMaskedV2 {
	id: string;
	tenant_id: string;
	publisher_id: string;
	name: string | null;
	key_preview: string;
	scopes: PublisherScopeV2[];
	active: boolean;
	created_at: number;
	revoked_at: number | null;
}

export interface PublisherApiKeyCreatedV2Response {
	key: PublisherApiKeyMaskedV2;
	raw_key: string;
}

export interface PublisherApiKeysV2ListResponse {
	publisher_id: string;
	count: number;
	keys: PublisherApiKeyMaskedV2[];
}

export interface PublisherApiEndpointDescriptor {
	method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
	path: string;
	description: string;
	required_scope: PublisherScopeV2;
}

export interface PublisherApiEndpointsResponse {
	count: number;
	endpoints: PublisherApiEndpointDescriptor[];
	scopes: PublisherScopeV2[];
}

export interface PublisherApiAccessLogEntry {
	id: string;
	tenant_id: string;
	publisher_id: string;
	key_id: string;
	method: string;
	path: string;
	status_code: number;
	ts: number;
}

export interface PublisherApiAccessLogResponse {
	publisher_id: string;
	count: number;
	limit: number;
	entries: PublisherApiAccessLogEntry[];
}

export interface CreateKeyV2Input {
	publisherId: string;
	name: string;
	scopes: string[];
}

export interface CreateKeyV2Body {
	publisher_id: string;
	name: string;
	scopes: PublisherScopeV2[];
}

export interface CreateKeyV2ParseOk {
	success: true;
	data: CreateKeyV2Body;
}
export interface CreateKeyV2ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type CreateKeyV2ParseResult =
	| CreateKeyV2ParseOk
	| CreateKeyV2ParseErr;

const PUB_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

export const createKeyV2FormSchema = {
	safeParse(input: CreateKeyV2Input): CreateKeyV2ParseResult {
		const errors: Record<string, string> = {};

		const publisherRaw =
			typeof input.publisherId === "string" ? input.publisherId.trim() : "";
		if (publisherRaw.length === 0) {
			errors.publisherId = "Publisher ID is required.";
		} else if (!PUB_ID_RE.test(publisherRaw)) {
			errors.publisherId =
				"Publisher ID may only contain letters, numbers, '-' and '_'.";
		}

		const nameRaw = typeof input.name === "string" ? input.name.trim() : "";
		if (nameRaw.length === 0) {
			errors.name = "Name is required.";
		} else if (nameRaw.length > 128) {
			errors.name = "Name must be 128 characters or fewer.";
		}

		const cleanedScopes: PublisherScopeV2[] = [];
		if (!Array.isArray(input.scopes) || input.scopes.length === 0) {
			errors.scopes = "Pick at least one scope.";
		} else {
			for (const raw of input.scopes) {
				if (!isValidScopeV2(raw)) {
					errors.scopes = `Unknown scope: ${String(raw)}`;
					break;
				}
				if (!cleanedScopes.includes(raw)) cleanedScopes.push(raw);
			}
		}

		if (Object.keys(errors).length > 0) {
			return { success: false, errors };
		}
		return {
			success: true,
			data: {
				publisher_id: publisherRaw,
				name: nameRaw,
				scopes: cleanedScopes,
			},
		};
	},
};

export async function fetchKeysV2(
	publisherId: string,
): Promise<PublisherApiKeyMaskedV2[]> {
	const id = publisherId.trim();
	if (!id) return [];
	try {
		const res = await apiClient.get<PublisherApiKeysV2ListResponse>(
			`/publisher-api/keys?publisher_id=${encodeURIComponent(id)}`,
		);
		return Array.isArray(res?.keys) ? res.keys : [];
	} catch (err) {
		if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
			return [];
		}
		throw err;
	}
}

export async function createKeyV2(
	body: CreateKeyV2Body,
): Promise<PublisherApiKeyCreatedV2Response> {
	return apiClient.post<PublisherApiKeyCreatedV2Response>(
		"/publisher-api/v2/keys",
		{
			publisher_id: body.publisher_id,
			name: body.name,
			scopes: body.scopes,
		},
	);
}

export async function deleteKeyV2(
	id: string,
): Promise<{ id: string; revoked_at: number; revoked: boolean }> {
	return apiClient.delete<{
		id: string;
		revoked_at: number;
		revoked: boolean;
	}>(`/publisher-api/keys/${encodeURIComponent(id)}`);
}

export async function fetchEndpoints(): Promise<PublisherApiEndpointsResponse> {
	return apiClient.get<PublisherApiEndpointsResponse>(
		"/publisher-api/endpoints",
	);
}

export async function fetchAccessLog(
	publisherId: string,
	limit: number = 100,
): Promise<PublisherApiAccessLogResponse> {
	const id = publisherId.trim();
	const safeLimit = Number.isFinite(limit) && limit > 0 ? Math.min(500, Math.floor(limit)) : 100;
	if (!id) {
		return {
			publisher_id: "",
			count: 0,
			limit: safeLimit,
			entries: [],
		};
	}
	try {
		return await apiClient.get<PublisherApiAccessLogResponse>(
			`/publisher-api/access-log?publisher_id=${encodeURIComponent(id)}&limit=${safeLimit}`,
		);
	} catch (err) {
		if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
			return {
				publisher_id: id,
				count: 0,
				limit: safeLimit,
				entries: [],
			};
		}
		throw err;
	}
}

export function formatMaskedKeyV2(row: PublisherApiKeyMaskedV2): string {
	return row.key_preview;
}

export function formatAccessLogTimestamp(ts: number): string {
	if (!Number.isFinite(ts) || ts <= 0) return "-";
	const d = new Date(ts * 1000);
	// ISO-ish without millis.
	return `${d.toISOString().slice(0, 19).replace("T", " ")}Z`;
}
